'use client'

import { Fragment, useState } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { BanknoteArrowUp, Check, FileText, Handshake, Truck, X } from 'lucide-react'
import { formatDateLong } from '@/utils/util'

type TermsType = 'delivery' | 'payment' | 'service'

interface TermsDrawerProps {
  isOpen: boolean
  onClose: () => void
  type: TermsType | null
  terms: { delivery?: string, payment?: string, service?: string }
  vendor?: any
}

const TYPE_META = {
  delivery: { title: 'Хүргэлтийн нөхцөл', eyebrow: 'Хүргэлт', Icon: Truck },
  payment: { title: 'Төлбөрийн нөхцөл', eyebrow: 'Төлбөр', Icon: BanknoteArrowUp },
  service: { title: 'Үйлчилгээний нөхцөл', eyebrow: 'Үйлчилгээ', Icon: Handshake },
} as const

const TABS: { id: TermsType, label: string, Icon: typeof Truck }[] = [
  { id: 'delivery', label: 'Хүргэлт', Icon: Truck },
  { id: 'payment', label: 'Төлбөр', Icon: BanknoteArrowUp },
  { id: 'service', label: 'Үйлчилгээ', Icon: Handshake },
]

export default function TermsDrawer({ isOpen, onClose, type, terms, vendor }: TermsDrawerProps) {
  const [activeType, setActiveType] = useState<TermsType>(type ?? 'delivery')
  const [prevType, setPrevType] = useState<TermsType | null>(type)

  if (type && type !== prevType) {
    setPrevType(type)
    setActiveType(type)
  }

  const meta = TYPE_META[activeType]
  const HeaderIcon = meta.Icon
  const rawContent = terms[activeType]
  const hasContent = !!rawContent && rawContent.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim().length > 0

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-[2px]" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 flex justify-end">
          <TransitionChild
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel
              className="relative flex w-full max-w-115 flex-col overflow-hidden rounded-l-2xl"
              style={{
                background: 'linear-gradient(180deg, #16161c 0%, #0c0c10 100%)',
                border: '1px solid rgba(255,255,255,0.13)',
                boxShadow: '0 40px 100px -20px rgba(0,0,0,0.75)',
              }}
            >
              <div
                className="sticky top-0 z-10 flex h-14 items-center justify-between gap-3 px-4"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(12,12,16,0.95)', backdropFilter: 'blur(8px)' }}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-lg"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' }}
                  >
                    <HeaderIcon className="h-4 w-4" strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <div className="mb-1 text-[10px] font-semibold uppercase leading-none tracking-[0.14em]" style={{ color: '#a8a8b3' }}>{meta.eyebrow}</div>
                    <div className="truncate text-[15px] font-semibold leading-none tracking-[-0.005em] text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{meta.title}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-full transition-colors cursor-pointer"
                  style={{ color: 'rgba(255,255,255,0.5)' }}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="px-3 pt-3">
                <div
                  className="flex items-center gap-1 rounded-xl p-1"
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}
                >
                  {TABS.map(t => {
                    const active = activeType === t.id
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setActiveType(t.id)}
                        className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg text-[13px] font-medium transition-all cursor-pointer"
                        style={active
                          ? { background: 'rgba(255,255,255,0.08)', color: 'white' }
                          : { background: 'transparent', color: 'rgba(255,255,255,0.55)' }}
                      >
                        <t.Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
                        {t.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="px-6 pt-5 pb-2">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: '#a8a8b3' }}>Дэлгэрэнгүй</div>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain px-6 pb-6 scrollbar-dark">
                {hasContent ? (
                  <div
                    className="prose prose-invert prose-sm max-w-none text-[14px] leading-6"
                    style={{ color: 'rgba(255,255,255,0.85)' }}
                    dangerouslySetInnerHTML={{ __html: rawContent! }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <FileText className="h-8 w-8 mb-2" style={{ color: 'rgba(255,255,255,0.4)' }} />
                    <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.5)' }}>Мэдээлэл олдсонгүй</p>
                  </div>
                )}
              </div>

              <div
                className="flex items-center justify-between gap-3 p-3"
                style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.3)' }}
              >
                <span className="text-[11.5px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  {vendor?.createdAt ? `Дэлгүүр нээгдсэн: ${formatDateLong(new Date(vendor.createdAt))}` : ''}
                </span>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[12.5px] font-semibold cursor-pointer transition-opacity hover:opacity-90"
                  style={{ background: 'white', color: '#0a0a0c' }}
                >
                  Ойлголоо <Check className="h-3.5 w-3.5" />
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
